import { uniq } from "lodash-es";

import { getLocaleText } from "shared/boot/i18n";
import Colors, { mediumColors } from "shared/helpers/colors";
import { getFeatureId } from "shared/helpers/features";
import { isSocialBookmarkStream, isSocialStream } from "shared/helpers/stream";
import { type InstantInsightsReport } from "shared/resources";
import features from "shared/services/features";
import { type OrganisationFeaturesContentToggle } from "shared/stores/organisationFeatures";
import type { Stream } from "shared/types";
import { MentionType, type SocialMentionType } from "shared/types/mentions";

import { socialMedia as social } from "./socialMedia";
import {
  type ExternalSource,
  type InfluencerSocialMedium,
  type MagazineMedium,
  type Medium,
  type MediumByClass,
  MediumField,
  type MediumName,
  type MediumPlatform,
  type OnlineMedium,
  type PodcastMedium,
  type PrintMedium,
  type RadioMedium,
  type SocialPlatform,
  type TvMedium,
} from "./types";

export const online: OnlineMedium = {
  categoryLocationFiltersEnabled: true,
  classField: "website",
  color: mediumColors.online,
  displayExcerptsFeature: "read_online_article",
  featureFlag: "read_online_article",
  field: MediumField.ONLINE,
  filterEnabled: true,
  hasNotification: true,
  icon: `${import.meta.env.VITE_ASSETS_URL}/icons/online.svg`,
  label: "Online",
  medium: "Online",
  relatedCamelCaseFields: ["Article"],
  relatedFields: [MentionType.article],
  relatedLabels: ["Online Article"],
  relatedTypes: [
    {
      appLabel: "Online Articles",
      checked: true,
      featureFlag: "read_online_article",
      featureId: getFeatureId("read_online_article"),
      field: MentionType.article,
      label: "Online Article",
    },
  ],
  resource: "websites",
  sourceLabel: "Publisher",
  triggerAlert: true,
};

export const print: PrintMedium = {
  categoryLocationFiltersEnabled: true,
  classField: "newspaper",
  color: mediumColors.print,
  createActivity: false,
  displayExcerptsFeature: "display_print_excerpts",
  featureAllowanceFlag: "view_print_articles",
  featureFlag: "view_paper_articles",
  featureName: "Print Items",
  field: MediumField.PRINT,
  filterEnabled: true,
  hasNotification: true,
  icon: `${import.meta.env.VITE_ASSETS_URL}/icons/print.svg`,
  label: "Print",
  medium: "Print",
  relatedCamelCaseFields: ["PaperArticle"],
  relatedFields: [MentionType.paper_article],
  relatedLabels: ["Print Article"],
  relatedTypes: [
    {
      appLabel: "Print Articles",
      checked: true,
      featureFlag: "view_paper_articles",
      featureId: getFeatureId("view_paper_articles"),
      field: MentionType.paper_article,
      label: "Paper Article",
    },
  ],
  resource: "newspapers",
  sourceLabel: "Publication",
  triggerAlert: true,
  viewFeatureFlag: "view_paper_articles",
  viewPopupAllowOverspend: true,
  viewPopupShown: "always",
  viewWarningFeatureFlag: "view_print_articles_warning",
};

export const magazine: MagazineMedium = {
  categoryLocationFiltersEnabled: true,
  classField: "magazine",
  color: mediumColors.magazine,
  createActivity: false,
  displayExcerptsFeature: "display_magazine_excerpts",
  featureAllowanceFlag: "view_print_articles",
  featureFlag: "view_magazine_articles",
  featureName: "Magazine Items",
  field: MediumField.MAGAZINE,
  filterEnabled: true,
  hasNotification: true,
  icon: `${import.meta.env.VITE_ASSETS_URL}/icons/magazine.svg`,
  label: "Magazine",
  medium: "Magazine",
  relatedCamelCaseFields: ["MagazineArticle"],
  relatedFields: [MentionType.magazine_article],
  relatedLabels: ["Magazine Article"],
  relatedTypes: [
    {
      appLabel: "Magazine Articles",
      checked: true,
      featureFlag: "view_magazine_articles",
      featureId: getFeatureId("view_magazine_articles"),
      field: MentionType.magazine_article,
      label: "Magazine Article",
    },
  ],
  resource: "magazines",
  sourceLabel: "Magazine",
  triggerAlert: true,
  viewFeatureFlag: "view_magazine_articles",
  viewPopupAllowOverspend: true,
  viewPopupShown: "always",
  viewWarningFeatureFlag: "view_print_articles_warning",
};

export const podcast: PodcastMedium = {
  categoryLocationFiltersEnabled: true,
  classField: "podcast",
  color: mediumColors.podcast,
  createActivity: false,
  displayExcerptsFeature: "podcast_mentions",
  featureFlag: "podcast_mentions",
  featureName: "Podcast Items",
  field: MediumField.PODCAST,
  filterEnabled: true,
  hasNotification: true,
  icon: `${import.meta.env.VITE_ASSETS_URL}/icons/podcast.svg`,
  label: "Podcast",
  medium: "Podcast",
  relatedCamelCaseFields: ["PodcastEpisode"],
  relatedFields: [MentionType.podcast_episode],
  relatedLabels: ["Podcast Episode"],
  relatedTypes: [
    {
      appLabel: "Podcast Episodes",
      checked: true,
      featureFlag: "view_podcast_episode",
      featureId: getFeatureId("view_podcast_episode"),
      field: MentionType.podcast_episode,
      label: "Podcast Episode",
    },
  ],
  resource: "podcasts",
  sourceLabel: "Podcast",
  triggerAlert: true,
  viewPopupAllowOverspend: true,
  viewPopupShown: "always",
};

export const tv: TvMedium = {
  categoryLocationFiltersEnabled: true,
  classField: "tv_channel",
  color: mediumColors.tv,
  createActivity: true,
  displayExcerptsFeature: "tv_mentions",
  featureAllowanceFlag: "play_tv_clips",
  featureFlag: "tv_mentions",
  featureName: "TV Replay",
  field: MediumField.TV,
  filterEnabled: true,
  hasNotification: true,
  icon: `${import.meta.env.VITE_ASSETS_URL}/icons/tv.svg`,
  label: "TV",
  medium: "TV",
  relatedCamelCaseFields: [
    "TvCaption",
    "TvSuper",
    "TvSegment",
    "TvLogoAppearance",
    "TvStory",
  ],
  relatedFields: [
    MentionType.tv_caption,
    MentionType.tv_super,
    "tv_media_request",
    MentionType.tv_logo_appearance,
    MentionType.tv_story,
  ],
  relatedLabels: [
    "TV Caption",
    "TV Super",
    "TV Media Request",
    "Tv Logo Appearance",
    "TV Story",
  ],
  relatedTypes: [
    {
      appLabel: "Spoken",
      checked: true,
      featureFlag: "view_tv_caption",
      field: MentionType.tv_caption,
      label: "Caption",
    },
    {
      appLabel: "On-screen text",
      checked: false,
      featureFlag: "view_tv_super",
      field: MentionType.tv_super,
      label: "Super",
    },
    {
      appLabel: "On-screen logos",
      checked: false,
      featureFlag: "view_tv_logo_appearance",
      field: MentionType.tv_logo_appearance,
      label: "Logo Appearance",
    },
    {
      appLabel: "TV Stories",
      checked: true,
      featureFlag: "has_broadcast_stories",
      field: MentionType.tv_story,
      label: "TV Stories",
    },
  ],
  resource: "tv_channels",
  sourceLabel: "Channel",
  triggerAlert: true,
  viewFeatureFlag: "play_tv_clips",
};

export const radio: RadioMedium = {
  categoryLocationFiltersEnabled: true,
  classField: "radio_station",
  color: mediumColors.radio,
  createActivity: true,
  displayExcerptsFeature: "radio_excerpts",
  featureAllowanceFlag: "play_radio_clips",
  featureFlag: "radio_mentions",
  featureName: "Radio Replay",
  field: MediumField.RADIO,
  filterEnabled: true,
  hasNotification: true,
  icon: `${import.meta.env.VITE_ASSETS_URL}/icons/radio.svg`,
  label: "Radio",
  medium: "Radio",
  relatedCamelCaseFields: ["RadioClip", "RadioSegment", "RadioStory"],
  relatedFields: [
    MentionType.radio_clip,
    "radio_media_request",
    MentionType.radio_story,
  ],
  relatedLabels: ["Radio Clip", "Radio Media Request", "Radio Story"],
  relatedTypes: [
    {
      appLabel: "Radio Clip",
      checked: true,
      featureFlag: "view_radio_clip",
      field: MentionType.radio_clip,
      label: "Radio Clip",
    },
    {
      appLabel: "Radio Stories",
      checked: true,
      featureFlag: "has_broadcast_stories",
      field: MentionType.radio_story,
      label: "Radio Stories",
    },
  ],
  resource: "radio_stations",
  sourceLabel: "Station",
  triggerAlert: true,
  viewFeatureFlag: "play_radio_clips",
};

export const influencerSocial: InfluencerSocialMedium = {
  categoryLocationFiltersEnabled: true,
  color: mediumColors.social,
  displayExcerptsFeature: "social_mentions",
  featureFlag: "social_mentions",
  field: MediumField.SOCIAL,
  filterEnabled: true,
  hasNotification: true,
  icon: `${import.meta.env.VITE_ASSETS_URL}/icons/social.svg`,
  label: "Influencer Social",
  medium: "Social",
  relatedCamelCaseFields: [
    "Tweet",
    "FacebookPost",
    "YoutubeVideo",
    "InstagramPost",
    "InstagramComment",
    "RedditPost",
    "RedditPostComment",
    "ExordeRedditPost",
    "ExordeRedditPostComment",
    "BlogPost",
    "ForumPost",
    "BlueskyPost",
    "LinkedinPost",
    "LinkedinComment",
    "TiktokVideo",
    "TiktokComment",
  ],
  relatedFields: [
    MentionType.tweet,
    MentionType.facebook_post,
    MentionType.youtube_video,
    MentionType.instagram_post,
    MentionType.instagram_comment,
    MentionType.reddit_post,
    MentionType.reddit_post_comment,
    MentionType.exorde_reddit_post,
    MentionType.exorde_reddit_post_comment,
    MentionType.blog_post,
    MentionType.forum_post,
    MentionType.bluesky_post,
    MentionType.linkedin_post,
    MentionType.linkedin_comment,
    MentionType.tiktok_video,
    MentionType.tiktok_comment,
  ],
  relatedLabels: [
    "Tweet",
    "FacebookPost",
    "YoutubeVideo",
    "InstagramPost",
    "InstagramComment",
    "RedditPost",
    "RedditPostComment",
    "ExordeRedditPost",
    "ExordeRedditPostComment",
    "BlogPost",
    "ForumPost",
    "BlueskyPost",
    "LinkedinPost",
    "LinkedinComment",
    "TiktokVideo",
    "TiktokComment",
  ],
  relatedTypes: [
    {
      appLabel: "Twitter",
      checked: true,
      color: "#77BDF1",
      featureFlag: "view_tweet",
      field: MentionType.tweet,
      icon: `${import.meta.env.VITE_ASSETS_URL}/icons/twitter.svg`,
      label: "Tweet",
    },
    {
      appLabel: "Facebook",
      checked: true,
      color: "#61749D",
      featureFlag: "view_facebook_post",
      field: MentionType.facebook_post,
      icon: `${import.meta.env.VITE_ASSETS_URL}/icons/facebook.svg`,
      label: "Facebook Post",
    },
    {
      appLabel: "Instagram",
      checked: true,
      color: "#E3787C",
      featureFlag: "view_instagram_post",
      field: MentionType.instagram_post,
      icon: `${import.meta.env.VITE_ASSETS_URL}/icons/instagram.svg`,
      label: "Instagram Post",
    },
    {
      appLabel: "Youtube",
      checked: true,
      color: "#D54B3F",
      featureFlag: "view_youtube_video",
      field: MentionType.youtube_video,
      icon: `${import.meta.env.VITE_ASSETS_URL}/icons/youtube.svg`,
      label: "YouTube Video",
    },
    {
      appLabel: "Reddit Posts",
      checked: true,
      color: Colors.reddit,
      featureFlag: "view_reddit_post",
      field: MentionType.reddit_post,
      hidden: true,
      icon: `${import.meta.env.VITE_ASSETS_URL}/icons/exorde-reddit.svg`,
      label: "Reddit Post",
    },
    {
      appLabel: "Reddit Comments",
      checked: true,
      color: Colors.reddit,
      featureFlag: "view_reddit_post",
      field: MentionType.reddit_post_comment,
      hidden: true,
      icon: `${import.meta.env.VITE_ASSETS_URL}/icons/exorde-reddit.svg`,
      label: "Reddit Post Comment",
    },
    {
      appLabel: "Exorde Reddit Posts",
      checked: true,
      color: Colors.reddit,
      featureFlag: "view_reddit_post",
      field: MentionType.exorde_reddit_post,
      hidden: true,
      icon: `${import.meta.env.VITE_ASSETS_URL}/icons/exorde-reddit.svg`,
      label: "Exorde Reddit Post",
    },
    {
      appLabel: "Exorde Reddit Comments",
      checked: true,
      color: Colors.reddit,
      featureFlag: "view_reddit_post",
      field: MentionType.exorde_reddit_post_comment,
      hidden: true,
      icon: `${import.meta.env.VITE_ASSETS_URL}/icons/exorde-reddit.svg`,
      label: "Exorde Reddit Post Comment",
    },
    {
      appLabel: "Blog Posts",
      checked: true,
      color: "#F06A35",
      featureFlag: "view_blog_post",
      field: MentionType.blog_post,
      hidden: true,
      icon: `${import.meta.env.VITE_ASSETS_URL}/icons/blog.svg`,
      label: "Blog Post",
    },
    {
      appLabel: "Forum Posts",
      checked: true,
      color: "#FFB649",
      featureFlag: "view_forum_post",
      field: MentionType.forum_post,
      hidden: true,
      icon: `${import.meta.env.VITE_ASSETS_URL}/icons/forum.svg`,
      label: "Forum Post",
    },
    {
      appLabel: "Bluesky Posts",
      checked: true,
      color: "#1285FE",
      featureFlag: "view_bluesky_post",
      field: MentionType.bluesky_post,
      hidden: true,
      icon: `${import.meta.env.VITE_ASSETS_URL}/icons/bluesky.svg`,
      label: "Bluesky Post",
    },
    {
      appLabel: "Linkedin Posts",
      checked: true,
      color: "#0274B3",
      featureFlag: "view_linkedin_post",
      field: MentionType.linkedin_post,
      hidden: true,
      icon: `${import.meta.env.VITE_ASSETS_URL}/icons/linkedin.svg`,
      label: "LinkedIn Post",
    },
    {
      appLabel: "Linkedin Comments",
      checked: true,
      color: "#0274B3",
      featureFlag: "view_linkedin_post",
      field: MentionType.linkedin_comment,
      hidden: true,
      icon: `${import.meta.env.VITE_ASSETS_URL}/icons/linkedin.svg`,
      label: "LinkedIn Comment",
    },
    {
      appLabel: "TikTok Posts",
      checked: true,
      color: "#FE2C55",
      featureFlag: "view_tiktok_video",
      field: MentionType.tiktok_video,
      hidden: true,
      icon: `${import.meta.env.VITE_ASSETS_URL}/icons/tiktok.svg`,
      label: "TikTok Post",
    },
    {
      appLabel: "TikTok Comments",
      checked: true,
      color: "#FE2C55",
      featureFlag: "view_tiktok_video",
      field: MentionType.tiktok_comment,
      hidden: true,
      icon: `${import.meta.env.VITE_ASSETS_URL}/icons/tiktok.svg`,
      label: "TikTok Comment",
    },
  ],
  sourceLabel: "Source",
  triggerAlert: false,
};

export const externalItem: ExternalSource = {
  categoryLocationFiltersEnabled: false,
  classField: MentionType.external_item,
  color: "#E5EAEF",
  createActivity: false,
  featureFlag: "has_external_items",
  featureName: "External Items",
  filterEnabled: false,
  hasNotification: true,
  icon: `${import.meta.env.VITE_ASSETS_URL}/icons/external-item.svg`,
  label: "External Item",
  relatedCamelCaseFields: ["ExternalItem"],
  relatedFields: [MentionType.external_item],
  relatedLabels: [MentionType.external_item],
  relatedTypes: [
    {
      appLabel: "External Items",
      checked: true,
      featureFlag: "has_external_items",
      featureId: getFeatureId("has_external_items"),
      field: MentionType.external_item,
      label: "External Item",
    },
  ],
  resource: "external_items",
  sourceLabel: "External Item",
  triggerAlert: true,
  viewPopupAllowOverspend: true,
  viewPopupShown: "always",
};

const sources: Medium[] = [
  online,
  print,
  tv,
  radio,
  magazine,
  influencerSocial,
  podcast,
];

export interface DomainAuthorityOption {
  key: string;
  label?: string;
  max: number;
  min: number;
}

const DOMAIN_AUTHORITY_OPTIONS: DomainAuthorityOption[] = [
  {
    key: "low",
    max: 20,
    min: 1,
  },
  {
    key: "average",
    max: 40,
    min: 21,
  },
  {
    key: "good",
    max: 60,
    min: 41,
  },
  {
    key: "strong",
    max: 80,
    min: 61,
  },
  {
    key: "excellent",
    max: 100,
    min: 81,
  },
];

function getDomainAuthorityOptions(): DomainAuthorityOption[] {
  return DOMAIN_AUTHORITY_OPTIONS.map((option) => ({
    ...option,
    label: getLocaleText(
      `stream_filters_media_domain_authority.range_options.${option.key}`,
      { max: option.max, min: option.min }
    ),
  }));
}

function isSourceWithClassField(source: Medium): source is MediumByClass {
  return "classField" in source;
}

const sourceByClass: {
  [key: string]: MediumByClass;
} = sources
  .filter((source) => isSourceWithClassField(source))
  .reduce(
    (lookup, source) => ({
      ...lookup,
      [(source as MediumByClass).classField]: source,
    }),
    {}
  );

const sourceByField: { [key: string]: Medium } = sources.reduce(
  (lookup, source) => ({
    ...lookup,
    [source.field]: source,
  }),
  {}
);

const sourceByMedium: { [key: string]: Medium } = sources.reduce(
  (lookup, source) => ({
    ...lookup,
    [source.medium]: source,
  }),
  {}
);

const sourceByRelatedFields: { [key: string]: Medium } = sources.reduce(
  (lookup, source) => {
    source.relatedFields.forEach((field) => {
      Object.assign(lookup, { [field]: source });
    });

    return lookup;
  },
  {}
);

function adminDefaultContentToggles(): OrganisationFeaturesContentToggle {
  const defaults: OrganisationFeaturesContentToggle = {};

  sources.forEach((source) => {
    defaults[`selected_${source.field}_content`] = source.relatedTypes.map(
      ({ field }) => field
    );
  });

  return defaults;
}

function adminDefaultCheckedContentToggles(): OrganisationFeaturesContentToggle {
  const defaults: OrganisationFeaturesContentToggle = {};

  sources.forEach((source) => {
    defaults[`selected_${source.field}_content`] = source.relatedTypes
      .filter(({ checked }) => checked)
      .map(({ field }) => field);
  });

  return defaults;
}

function defaultContentToggles(): OrganisationFeaturesContentToggle {
  const defaults: OrganisationFeaturesContentToggle = {};

  sources.forEach((source) => {
    if (features.has(source.featureFlag)) {
      defaults[`selected_${source.field}_content`] = source.relatedTypes
        .filter(
          (type) =>
            (!type.featureFlag || features.has(type.featureFlag)) &&
            !type.hidden
        )
        .map(({ field }) => field);
    }
  });

  return defaults;
}

function getSelectedContentForMedia(
  media: MediumName[]
): OrganisationFeaturesContentToggle {
  const defaults: OrganisationFeaturesContentToggle = defaultContentToggles();

  if (!media || media.length === 0) {
    return defaults;
  }

  const localMedia = media.map(
    (medium) => sourceByMedium[medium].field as string
  );

  const result: OrganisationFeaturesContentToggle = {};

  Object.keys(defaults).forEach((key) => {
    const typedKey = key as keyof OrganisationFeaturesContentToggle;

    if (
      localMedia.includes(key.replace("selected_", "").replace("_content", ""))
    ) {
      result[typedKey] = defaults[typedKey];
    } else {
      result[typedKey] =
        [] as OrganisationFeaturesContentToggle[typeof typedKey];
    }
  });

  return result;
}

function defaultSocialContentToggles(): OrganisationFeaturesContentToggle {
  const defaults: OrganisationFeaturesContentToggle = {};

  sources.forEach((source) => {
    if (features.has(source.featureFlag)) {
      defaults[`selected_${source.field}_content`] = [];
    }
  });

  if (features.has(social.featureFlag)) {
    defaults.selected_social_content = social.relatedTypes
      .filter((type) => !type.featureFlag || features.has(type.featureFlag))
      .map(({ field }) => field);
  }

  return defaults;
}

function defaultCheckedContentToggles(): OrganisationFeaturesContentToggle {
  const defaults: OrganisationFeaturesContentToggle = {};

  sources.forEach((source) => {
    if (features.has(source.featureFlag)) {
      defaults[`selected_${source.field}_content`] = source.relatedTypes
        .filter(
          (type) =>
            type.checked &&
            (!type.featureFlag || features.has(type.featureFlag)) &&
            !type.hidden
        )
        .map(({ field }) => field);
    }
  });

  return defaults;
}

export function getLocalisedSourceLabels(localSources: Medium[]): Medium[] {
  return localSources.map((source) => {
    const { relatedTypes } = source;

    const localisedRelatedTypes = relatedTypes.map((relatedType) => ({
      ...relatedType,
      appLabel: getLocaleText(
        `helpers.sources.${source.field}.related_type_${relatedType.field}_app_label`
      ),
      label: getLocaleText(
        `helpers.sources.${source.field}.related_type_${relatedType.field}_label`
      ),
    }));

    return {
      ...source,
      label: getLocaleText(`helpers.sources.${source.field}.label`),
      relatedTypes: localisedRelatedTypes,
    };
  });
}

function getLocalisedPlatformLabels(
  platforms: MediumPlatform[]
): MediumPlatform[] {
  return platforms.map((platform) => ({
    ...platform,
    label: getLocaleText(
      `helpers.sources.platform_labels.${platform.platform}`
    ),
  }));
}

function getSources({ ignoreFeatureCheck = false } = {}): Medium[] {
  const localisedSources = getLocalisedSourceLabels(sources);

  const enabledSources = localisedSources.filter(
    (source) => source.filterEnabled
  );

  if (ignoreFeatureCheck) return enabledSources;

  if (
    features.has("has_outreach") &&
    !features.hasAny(["has_media_platform", "has_social_platform"])
  ) {
    return enabledSources;
  }

  return localisedSources.filter((source) => features.has(source.featureFlag));
}

function getTypesForSources(selectedSources: Medium[]): string[] {
  const types = selectedSources.reduce<MentionType[]>(
    (acc, source) =>
      acc.concat(
        source.relatedTypes
          .filter((type) => features.has(type.featureFlag))
          .map(({ field }) => field)
      ),
    []
  );

  if (
    features.has("view_tv_logo_appearance") &&
    selectedSources.filter((source) => source.medium === "TV").length
  ) {
    types.push(MentionType.tv_logo_appearance);
  }

  return Array.from(new Set(types));
}

function getTypesFromSelectedContent(stream: Stream): string[] {
  const types = sources
    .reduce(
      (acc, source) =>
        acc.concat(stream[`selected_${source.field}_content`] as MentionType[]),
      [] as MentionType[]
    )
    .filter((type) => Boolean(type));

  return Array.from(new Set(types));
}

function getTypesForStreamFromSources(
  stream: Stream,
  selectedSources: Medium[]
): MentionType[] {
  const types = selectedSources
    .reduce((acc, source) => {
      const sourceTypes = stream[`${source.field}_content`]
        ? stream[`selected_${source.field}_content`]
        : [];

      return acc.concat(sourceTypes as MentionType[]);
    }, [] as MentionType[])
    .filter((type) => type?.length);

  return Array.from(new Set(types));
}

function getTypesForStream(stream: Stream): MentionType[] {
  const isSocialStreamType =
    isSocialStream(stream) || isSocialBookmarkStream(stream);

  const allowedSources = isSocialStreamType ? [social] : getSources();

  return getTypesForStreamFromSources(stream, allowedSources);
}

function getTypesForStreams(streams: Stream[]): MentionType[] {
  return uniq(streams.flatMap(getTypesForStream)).sort();
}

function getTypesForMedia(media: string[] = []): MentionType[] {
  const searchTypes: MentionType[] = [];

  sources.forEach((source) => {
    if (media.includes(source.medium)) {
      const enabledRelatedTypes = source.relatedTypes
        .filter(
          ({ featureFlag, hidden }) =>
            (!featureFlag || features.has(featureFlag)) && !hidden
        )
        .map(({ field }) => field);

      searchTypes.push(...enabledRelatedTypes);
    }
  });

  return searchTypes;
}

function getTypesForPlatform(platform: SocialPlatform): MentionType[] {
  const platformMatch = social.platforms.find(
    (socialPlatform) => socialPlatform.platform === platform
  );

  return convertSocialTypesToMentionTypes(platformMatch?.fields ?? []);
}

function getMediaForInstantInsightsReport(
  report: InstantInsightsReport
): MediumName[] {
  const reportSources = getSources()
    .filter((source) => report[`${source.field}Content`])
    .map((source) => source.medium);

  const newChartSettings = report.newChartSettings || [];

  const chartMediaTypes = newChartSettings.flatMap(
    (chart) => chart.types ?? []
  );

  const chartSources = getMediaFromTypes(chartMediaTypes);

  return [...new Set([...reportSources, ...chartSources])];
}

function getMediaForStream(stream: Stream): MediumName[] {
  return getSources()
    .filter((source) => stream[`${source.field}_content`])
    .map((source) => source.medium);
}

function getMediaForInsightsQuery(insightsQuery: any): string[] {
  return getSources()
    .filter((source) => insightsQuery[`${source.field}Content`])
    .map((source) => source.medium);
}

function getSourcesForStream(stream: Stream): Medium[] {
  return getSources().filter(({ field }) => stream[`${field}_content`]);
}

function getSource(type: string): Medium {
  const source = { ...sourceByRelatedFields[type] };
  const relatedIndex = source.relatedFields.indexOf(type);
  source.relatedLabel = source.relatedLabels[relatedIndex];
  source.relatedCamelCaseField = source.relatedCamelCaseFields[relatedIndex];

  return source;
}

function getSourceFromGroup(field: string): Medium {
  return sourceByField[field];
}

function getAllRelatedFields(): string[] {
  return getSources().reduce<string[]>(
    (acc, value) => [...acc, ...value.relatedFields],
    []
  );
}

function getSourceByField(field: string): Medium {
  return sourceByField[field];
}

function getSourceByClass(classField: string): MediumByClass {
  return sourceByClass[classField];
}

function getSocialPlatforms(): MediumPlatform[] {
  const platforms = getLocalisedPlatformLabels(social.platforms);

  return platforms.filter(({ featureFlag }) => features.has(featureFlag));
}

function getEnabledSocialPlatformsForStream(stream: Stream): MediumPlatform[] {
  return getSocialPlatforms().filter((platform) =>
    Boolean(
      stream.selected_social_content?.some((value) =>
        platform.fields.includes(value)
      )
    )
  );
}

function sourceIcon(sourceType: string): string {
  switch (sourceType) {
    case MentionType.facebook_post:
      return "ion-logo-facebook";
    case MentionType.instagram_comment:
    case MentionType.instagram_post:
      return `img:${import.meta.env.VITE_ASSETS_URL}/icons/instagram.svg`;
    case MentionType.reddit_post_comment:
      return "ion-logo-reddit";
    case MentionType.exorde_reddit_post:
    case MentionType.exorde_reddit_post_comment:
      return `img:${import.meta.env.VITE_ASSETS_URL}/icons/exorde-reddit.svg`;
    case MentionType.youtube_video:
      return "ion-logo-youtube";
    case MentionType.tweet:
      return `img:${import.meta.env.VITE_ASSETS_URL}/icons/twitterx-light.svg`;
    case MentionType.blog_post:
      return `img:${import.meta.env.VITE_ASSETS_URL}/icons/blog.svg`;
    case MentionType.forum_post:
      return `img:${import.meta.env.VITE_ASSETS_URL}/icons/forum.svg`;
    case MentionType.bluesky_post:
      return `img:${import.meta.env.VITE_ASSETS_URL}/icons/bluesky.svg`;
    case MentionType.linkedin_post:
    case MentionType.linkedin_comment:
      return `img:${import.meta.env.VITE_ASSETS_URL}/icons/linkedin.svg`;
    case MentionType.tiktok_video:
    case MentionType.tiktok_comment:
      return `img:${import.meta.env.VITE_ASSETS_URL}/icons/tiktok.svg`;
    default:
      return "ion-person";
  }
}

function socialIcon(socialType: SocialMentionType): string | undefined {
  const assetsUrl = `img:${import.meta.env.VITE_ASSETS_URL}`;

  const iconMap: Record<SocialMentionType, string> = {
    [MentionType.blog_post]: "blog",
    [MentionType.bluesky_post]: "bluesky",
    [MentionType.exorde_reddit_post]: "exorde-reddit",
    [MentionType.exorde_reddit_post_comment]: "exorde-reddit",
    [MentionType.facebook_post]: "facebook",
    [MentionType.forum_post]: "forum",
    [MentionType.instagram_comment]: "instagram",
    [MentionType.instagram_post]: "instagram",
    [MentionType.linkedin_comment]: "linkedin",
    [MentionType.linkedin_post]: "linkedin",
    [MentionType.reddit_post]: "exorde-reddit",
    [MentionType.reddit_post_comment]: "exorde-reddit",
    [MentionType.tiktok_comment]: "tiktok",
    [MentionType.tiktok_video]: "tiktok",
    [MentionType.tweet]: "twitter",
    [MentionType.youtube_video]: "youtube",
  };

  const iconName = iconMap[socialType];

  return iconName ? `${assetsUrl}/icons/${iconName}.svg` : undefined;
}

const externalItemIcon: string = `img:${
  import.meta.env.VITE_ASSETS_URL
}/icons/external-item.svg`;

function filterSocialPlatformsByTypes(types: string[] = []): MediumPlatform[] {
  const { platforms } = social;

  if (!types.length) {
    return platforms;
  }

  return platforms.filter((platform) =>
    platform.fields.some((field) => types.includes(field))
  );
}

function filterSocialPlatformsByLabels(
  labels: string[] = []
): MediumPlatform[] {
  const { platforms } = social;

  if (!labels.length) {
    return platforms;
  }

  return platforms.filter((platform) => labels.includes(platform.label));
}

function getMediaFromTypes(types: string[]): MediumName[] {
  const defaultTypes = getSources()
    .filter(({ relatedFields }) =>
      types?.some((type) => relatedFields.includes(type))
    )
    .map(({ medium }) => medium);

  if (
    features.has(social.featureFlag) &&
    social.relatedFields.some((field) => types.includes(field)) &&
    !defaultTypes.includes(social.medium)
  ) {
    return [...defaultTypes, social.medium];
  }

  return defaultTypes;
}

// Helper function to convert SocialMentionType to MentionType
function convertSocialTypesToMentionTypes(
  socialTypes: SocialMentionType[]
): MentionType[] {
  return socialTypes.map((type) => type as string as MentionType).sort();
}

// Helper function to convert SocialMentionType to MentionType
function convertMentionTypesToSocialTypes(
  mentionTypes: MentionType[]
): SocialMentionType[] {
  const socialTypes = social.relatedFields.filter((field) =>
    mentionTypes.includes(field as MentionType)
  );

  return socialTypes.map((type) => type as string as SocialMentionType).sort();
}

function isWebVideoFileUrl(url: string): boolean {
  if (!url) return false;

  const videoExtensions = ["mp4", "webm", "ogg", "m4v", "mov"];

  const lowerUrl = url.toLowerCase();

  return videoExtensions.some((ext) => lowerUrl.endsWith(ext));
}

export default sources;

export {
  adminDefaultCheckedContentToggles,
  adminDefaultContentToggles,
  convertMentionTypesToSocialTypes,
  convertSocialTypesToMentionTypes,
  defaultCheckedContentToggles,
  defaultContentToggles,
  defaultSocialContentToggles,
  externalItemIcon,
  filterSocialPlatformsByLabels,
  filterSocialPlatformsByTypes,
  getAllRelatedFields,
  getDomainAuthorityOptions,
  getEnabledSocialPlatformsForStream,
  getMediaForInsightsQuery,
  getMediaForInstantInsightsReport,
  getMediaForStream,
  getMediaFromTypes,
  getSelectedContentForMedia,
  getSocialPlatforms,
  getSource,
  getSourceByClass,
  getSourceByField,
  getSourceFromGroup,
  getSources,
  getSourcesForStream,
  getTypesForMedia,
  getTypesForPlatform,
  getTypesForSources,
  getTypesForStream,
  getTypesForStreamFromSources,
  getTypesForStreams,
  getTypesFromSelectedContent,
  isWebVideoFileUrl,
  socialIcon,
  sourceByMedium,
  sourceIcon,
  sources,
};
